import * as React from 'react'
import { toast } from 'sonner'
import { getTranscript, updateTranscript } from '@/client/sdk.gen'
import type { TranscriptResponse } from '@/client/types.gen'
import { useSocket } from '@/context'
import { useTranslation } from '@/hooks/useTranslation'
import type { ArtifactStatusUpdateEvent } from '@/types/websocket'
import { extractErrorMessage } from '@/utils/error'

export interface UseTranscriptResult {
	transcript: TranscriptResponse | null
	loading: boolean
	error: string | null
	retry: () => Promise<void>
	saveTranscript: (content: string, options?: { silent?: boolean }) => Promise<boolean>
	saving: boolean
}

export function useTranscript(
	projectId: string | undefined,
	artifactId: string | null | undefined
): UseTranscriptResult {
	const { t } = useTranslation()
	const { socket, connected } = useSocket()
	const [transcript, setTranscript] = React.useState<TranscriptResponse | null>(null)
	const [loading, setLoading] = React.useState(false)
	const [error, setError] = React.useState<string | null>(null)
	const [saving, setSaving] = React.useState(false)
	const isSavingRef = React.useRef(false)

	const fetchTranscript = React.useCallback(
		async (isInitialLoad = false, isCancelled?: () => boolean) => {
			if (!projectId || !artifactId) return
			if (isInitialLoad) {
				setLoading(true)
				setError(null)
			}
			try {
				const response = await getTranscript({
					path: { project_id: projectId, artifact_id: artifactId },
				})
				if (isCancelled?.()) return
				if (response.error) {
					console.error('Transcript fetch failed:', response.error)
					setError(extractErrorMessage(response.error, t('pages.projectDetail.transcript.errorLoading')))
					setTranscript(null)
				} else if (response.data) {
					setTranscript(response.data)
				}
			} catch (err) {
				if (isCancelled?.()) return
				console.error('Transcript fetch failed:', err)
				setError(extractErrorMessage(err, t('pages.projectDetail.transcript.errorLoading')))
				setTranscript(null)
			} finally {
				if (isInitialLoad && !isCancelled?.()) {
					setLoading(false)
				}
			}
		},
		[projectId, artifactId, t]
	)

	React.useEffect(() => {
		if (!projectId || !artifactId) {
			setTranscript(null)
			setError(null)
			return
		}
		let cancelled = false
		const checkCancelled = () => cancelled
		fetchTranscript(true, checkCancelled)
		return () => {
			cancelled = true
		}
	}, [projectId, artifactId, fetchTranscript])

	// Refetch when artifact status changes to completed/failed (transcript status is derived from artifact)
	React.useEffect(() => {
		if (!socket || !connected || !projectId || !artifactId) return
		const handleStatusUpdate = (data: ArtifactStatusUpdateEvent) => {
			if (
				data.project_id === projectId &&
				data.artifact_id === artifactId &&
				(data.status === 'completed' || data.status === 'failed')
			) {
				fetchTranscript(false).catch((err) => {
					console.error('Transcript fetch failed:', err)
					setError(extractErrorMessage(err, t('pages.projectDetail.transcript.errorLoading')))
					setTranscript(null)
				})
			}
		}
		socket.on('artifact:status:update', handleStatusUpdate)
		return () => {
			socket.off('artifact:status:update', handleStatusUpdate)
		}
	}, [socket, connected, projectId, artifactId, fetchTranscript, t])

	const retry = React.useCallback(() => fetchTranscript(true), [fetchTranscript])

	const saveTranscript = React.useCallback(
		async (content: string, options?: { silent?: boolean }): Promise<boolean> => {
			if (!projectId || !artifactId) return false
			if (isSavingRef.current) return false
			isSavingRef.current = true
			setSaving(true)
			const silent = options?.silent ?? false
			try {
				const response = await updateTranscript({
					path: { project_id: projectId, artifact_id: artifactId },
					body: { content },
				})
				if (response.error) {
					if (!silent) {
						toast.error(
							extractErrorMessage(response.error, t('pages.projectDetail.transcript.errorSaving'))
						)
					}
					return false
				}
				if (response.data) {
					setTranscript(response.data)
					if (!silent) {
						toast.success(t('pages.projectDetail.transcript.saved'))
					}
					return true
				}
				return false
			} catch (err) {
				if (!silent) {
					toast.error(extractErrorMessage(err, t('pages.projectDetail.transcript.errorSaving')))
				}
				return false
			} finally {
				isSavingRef.current = false
				setSaving(false)
			}
		},
		[projectId, artifactId, t]
	)

	return {
		transcript,
		loading,
		error,
		retry,
		saveTranscript,
		saving,
	}
}
